/**
 * Live chat-feed preview for the Settings tab. Shows chat lines the API ingests
 * (via WebSocket), split into two windows — one for Twitch, one for YouTube —
 * highlighting the ones that matched the command and added a request. Each pane
 * auto-scrolls to its newest line unless the user has scrolled up.
 *
 * In local dev it also renders a small "send a test line" form (import.meta.env.
 * DEV) that hits POST /chat/dev-inject, so the feed is previewable without a
 * live provider connection — the production build omits it.
 */
import { useEffect, useRef, useState } from "react";
import { api, type ChatFeedMessage } from "../lib/api";
import { useChatFeed } from "../hooks/useChatFeed";

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

export function ChatFeed({ channelId }: { channelId: string }) {
  const { messages, live } = useChatFeed(channelId);

  return (
    <div className="panel">
      <div className="row" style={{ alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>Chat feed</h2>
        <span className={`live ${live ? "on" : ""}`}>
          <span className="dot" />
          {live ? "watching" : "connecting…"}
        </span>
      </div>

      <div className="chat-feeds">
        <FeedPane
          label="Twitch"
          platform="twitch"
          messages={messages.filter((m) => m.platform === "twitch")}
        />
        <FeedPane
          label="YouTube"
          platform="youtube"
          messages={messages.filter((m) => m.platform === "youtube")}
        />
      </div>

      {import.meta.env.DEV && <DevInject channelId={channelId} />}
    </div>
  );
}

/** One platform's chat window: its own scroll buffer and auto-follow behavior. */
function FeedPane({
  label,
  platform,
  messages,
}: {
  label: string;
  platform: ChatFeedMessage["platform"];
  messages: ChatFeedMessage[];
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedToBottom = useRef(true);

  // Follow the newest line, but don't yank the view if the user scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }

  return (
    <div className="chat-feed-pane">
      <h3 className={`chat-feed-title plat-${platform}`}>{label}</h3>
      <div className="chat-feed" ref={scrollRef} onScroll={onScroll}>
        {messages.length === 0 ? (
          <p className="muted" style={{ margin: "auto", textAlign: "center" }}>
            Waiting for {label} chat…
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`chat-line ${m.matched ? "matched" : ""}`}>
              <span className="chat-time">{fmtTime(m.at)}</span>
              <span className={`chat-user plat-${m.platform}`}>{m.username}</span>
              <span className="chat-text">{m.text}</span>
              {m.matched && <span className="chat-added">added</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** LOCAL-ONLY tester: push a fake chat line so the feed has something to show. */
function DevInject({ channelId }: { channelId: string }) {
  const [username, setUsername] = useState("dev_tester");
  const [text, setText] = useState("!req https://youtu.be/dQw4w9WgXcQ");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api.chatDevInject({ channelId, username: username.trim(), text });
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat-devinject">
      <p className="muted" style={{ margin: "12px 0 8px" }}>
        Dev only — send a test chat line (connect chat first so ingestion is on):
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <div className="field" style={{ flex: "0 0 130px" }}>
          <label htmlFor="di-user">Username</label>
          <input id="di-user" value={username} onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div className="field" style={{ flex: "1 1 auto" }}>
          <label htmlFor="di-text">Message</label>
          <input id="di-text" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <button className="btn row-action" type="submit" disabled={busy || !text.trim()}>
          {busy ? "…" : "Send"}
        </button>
      </form>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}
